// featuredTourFilter.ts
// =====================
//
// Wave 14ca-fix50: Persistierter Difficulty-Filter fuer Featured Tours.
// User waehlt Set von Schwierigkeiten (z.B. {easy, intermediate}); ein Tour
// wird nur angezeigt wenn ihre `difficulty` (= MAX der enthaltenen Trails,
// backend-computed) im gewaehlten Set ist. Damit fallen Touren mit
// schwereren Trails automatisch raus.
//
// Storage: localStorage. Cross-Page-State (FeaturedToursPage + MapPage swipe-
// Nav) via `adt:featured-filter-changed`-Event. Default = null = "alle
// Schwierigkeiten zugelassen". Bei null filtern wir nicht.

import { useEffect, useState } from 'react';
import type { Difficulty } from '@adtu/shared';
import type { FeaturedTour } from './featuredTours';

const STORAGE_KEY = 'adt_featured_filter';
const CHANGE_EVENT = 'adt:featured-filter-changed';

/** Liest aktuell aktive Filter aus localStorage. null = kein Filter (alle
    Schwierigkeiten erlaubt). */
export function getFeaturedFilter(): Set<Difficulty> | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return null;
    if (arr.length === 0) return null;
    return new Set(arr as Difficulty[]);
  } catch {
    return null;
  }
}

/** Speichert Filter und feuert Event. null oder leer = Filter raus. */
export function setFeaturedFilter(allowed: Set<Difficulty> | null): void {
  if (typeof localStorage === 'undefined') return;
  if (!allowed || allowed.size === 0) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...allowed]));
  }
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* SSR */
  }
}

/** React-Hook: aktuelle Filter-Set, re-rendert bei Aenderungen aus
    irgendwo im Tree. */
export function useFeaturedFilter(): Set<Difficulty> | null {
  const [filter, setFilter] = useState<Set<Difficulty> | null>(() =>
    getFeaturedFilter(),
  );
  useEffect(() => {
    const onChange = () => setFilter(getFeaturedFilter());
    window.addEventListener(CHANGE_EVENT, onChange);
    // Cross-Tab-Sync: storage event feuert wenn andere Tabs aendern.
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return filter;
}

/** Filter-Logik: Tour passt durch wenn (a) kein Filter aktiv oder (b)
    tour.difficulty im erlaubten Set. */
export function tourMatchesFilter(
  tour: FeaturedTour,
  filter: Set<Difficulty> | null,
): boolean {
  if (!filter || filter.size === 0) return true;
  return filter.has(tour.difficulty as Difficulty);
}

/** Filtert eine Tour-Liste. */
export function filterFeaturedTours(
  tours: FeaturedTour[],
  filter: Set<Difficulty> | null,
): FeaturedTour[] {
  if (!filter || filter.size === 0) return tours;
  return tours.filter((t) => filter.has(t.difficulty as Difficulty));
}
